/**
 * Role-based access control (ES-7489, ES-7490).
 *
 * The API decides who may do what; the Portal only mirrors it to show and hide things. Areas and
 * their resolution rules must stay identical to Lyra-API (Api/Authorization/Areas.cs and PermissionMap.cs).
 */

/** Access level granted on an area */
export type AccessLevel = 'None' | 'Read' | 'ReadWrite';

/** Grants per area key as returned by GET /api/Permissions/Me, e.g. { "Devices": "ReadWrite" } */
export type PermissionGrants = Record<string, AccessLevel>;

/** Order of the access levels, for comparisons */
export const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = { None: 0, Read: 1, ReadWrite: 2 };

/** Area keys. A child ("Devices.Firmware") follows its parent ("Devices") unless it is granted itself. */
export const Areas = {
  Dashboard: 'Dashboard',
  DashboardCancelAlert: 'Dashboard.CancelAlert',
  Reports: 'Reports',
  ReportsStatistics: 'Reports.Statistics',
  ReportsActivity: 'Reports.Activity',
  Devices: 'Devices',
  DevicesFirmware: 'Devices.Firmware',
  DevicesNfc: 'Devices.Nfc',
  DevicesLocks: 'Devices.Locks',
  DevicesLocksTemporaryCodes: 'Devices.Locks.TemporaryCodes',
  DevicesBaseStations: 'Devices.BaseStations',
  DevicesAdvancedSettings: 'Devices.AdvancedSettings',
  AlarmRouting: 'AlarmRouting',
  ReceiverTeams: 'ReceiverTeams',
  Staff: 'Staff',
  StaffAccessRights: 'Staff.AccessRights',
  MobileWhitelist: 'MobileWhitelist',
  ActivityLog: 'ActivityLog',
  Cameras: 'Cameras',
  OnOn: 'OnOn',
  AnnaPerenna: 'AnnaPerenna',
  Locations: 'Locations',
} as const;

/** Translation key of an area's name, the same as the API's displayNameKey (for places without the registry) */
export function areaDisplayNameKey(key: string): string {
  return 'RBAC_AREA_' + key.replace('OnOn', 'Onon').replace(/([a-z])([A-Z])/g, '$1_$2').replace(/\./g, '_').toUpperCase();
}

/** Parent key of an area key, or null for a top-level area */
export function parentArea(key: string): string | null {
  const separator = key.lastIndexOf('.');
  return separator < 0 ? null : key.substring(0, separator);
}

/** Level of an area in a grant map: its own grant, else the nearest parent's, else None */
export function resolveGrant(grants: PermissionGrants, area: string): AccessLevel {
  for (let key: string | null = area; key !== null; key = parentArea(key)) {
    if (grants[key]) return grants[key];
  }
  return 'None';
}

/** One area of the registry, from GET /api/Permissions/Areas */
export interface IArea {
  key: string;
  parentKey?: string;
  displayNameKey: string;
}

/** A role from GET /api/AccessRoles: built-in (read-only) or custom */
export interface IAccessRole {
  id?: number;
  name: string;
  description?: string;
  icon?: string;
  builtIn: boolean;
  level?: number;
  grants: PermissionGrants;
  assignedStaffCount: number;
}

/** Body of POST / PUT /api/AccessRoles */
export interface ISaveAccessRole {
  name: string;
  description?: string;
  icon?: string;
  grants: PermissionGrants;
}

/** Name and icon of a custom role, from GET /api/AccessRoles/Symbols */
export interface IRoleSymbol {
  id: number;
  name: string;
  icon?: string | null;
}

/** GET /api/AccessRoles/Symbols: the customer's custom roles and who has them, without their grants */
export interface IRoleSymbols {
  roles: IRoleSymbol[];
  /** Custom role ID by staff ID */
  staffRoles: Record<string, number>;
}

/** GET /api/AccessRoles/Settings */
export interface IAccessRoleSettings {
  customRolesEnabled: boolean;
  maxCustomRoles: number;
}

/** Effective level of one area for a staff member, and where it comes from */
export interface IEffectiveGrant {
  level: AccessLevel;
  source: 'Role' | 'BuiltIn' | 'Override' | 'Tier' | 'None';
  fromArea?: string;
}

/** GET /api/AccessRoles/Staff/{staffId} */
export interface IStaffAccess {
  staffId: string;
  level: number;
  accessRoleId?: number;
  roleName: string;
  overrides: PermissionGrants;
  effective: Record<string, IEffectiveGrant>;
}

/** Translation key of a built-in role's name, by level */
export const BUILT_IN_ROLE_NAME_KEYS: Record<number, string> = {
  0: 'STAFF_READONLY',
  10: 'EveronMobileApp',
  40: 'STAFF_ONON_MAIN_USER',
  50: 'STAFF_USER',
  100: 'STAFF_MAIN_USER',
  101: 'STAFF_CUSTOMER_ADMIN',
  // Fixed roles managed by Everon admins (ES-7530)
  119: 'STAFF_EMAAC_OPERATOR',
  120: 'STAFF_EMAAC_ACCOUNT',
};
